import "firebase/analytics";

import "firebase/auth";

import { createContext, Dispatch, useContext } from "react";

import { UserRoleType } from "@sellar-io/api-v2/types";

export type SidePanelIconType = {
  type: keyof typeof SidePanelIconTypeEnum;
  number?: number;
};

export type User = {
  name: string | null;
  firebaseId: string;
  type: UserRoleType;
  SupplierId: number;
  email?: string;
  smsNumber?: string;
  profileImageUrl: string | null;
  UserNotificationPreferenceId: number;
};

export type SellarContextState = {
  loading: boolean;
  supplierId?: number;
  error: string;
  user?: User;
  sidePanelActive: boolean;
  sidePanelSticky: boolean;
  sidePanelIcon?: SidePanelIconType;
  navigationButtonHidden: boolean;
  lastExportAt?: Date;
  tabsActive: boolean;
  globalNavigation: boolean;
  businessSwitcherActive: boolean;
  selectedBusinessId?: number;
  unreadMessageCount: number;
};

export type SellarContextAction =
  | {
      type: SellarContextActionEnum.CHANGE_LAST_EXPORT_AT;
      lastExportAt: Date;
    }
  | {
      type: SellarContextActionEnum.CHANGE_ERROR;
      error: string;
    }
  | {
      type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ACTIVE;
      sidePanelActive: boolean;
    }
  | {
      type: SellarContextActionEnum.CHANGE_SIDE_PANEL_STICKY;
      sidePanelSticky: boolean;
    }
  | {
      type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ICON;
      sidePanelIcon: SidePanelIconType;
    }
  | {
      type: SellarContextActionEnum.CHANGE_NAVIGATION_BUTTON_HIDDEN;
      navigationButtonHidden: boolean;
    }
  | {
      type: SellarContextActionEnum.CHANGE_TABS_ACTIVE;
      tabsActive: boolean;
    }
  | {
      type: SellarContextActionEnum.CHANGE_BUSINESS_SWITCHER_ACTIVE;
      businessSwitcherActive: boolean;
    }
  | {
      type: SellarContextActionEnum.CHANGE_SELECTED_BUSINESS_ID;
      selectedBusinessId: number | undefined;
    }
  | {
      type: SellarContextActionEnum.CHANGE_UNREAD_MESSAGE_COUNT;
      unreadMessageCount: number;
    };

export enum SellarContextActionEnum {
  CHANGE_ERROR = "CHANGE_ERROR",
  CHANGE_SIDE_PANEL_ACTIVE = "CHANGE_SIDE_PANEL_ACTIVE",
  CHANGE_SIDE_PANEL_STICKY = "CHANGE_SIDE_PANEL_STICKY",
  CHANGE_SIDE_PANEL_ICON = "CHANGE_SIDE_PANEL_ICON",
  CHANGE_NAVIGATION_BUTTON_HIDDEN = "CHANGE_NAVIGATION_BUTTON_HIDDEN",
  CHANGE_LAST_EXPORT_AT = "CHANGE_LAST_EXPORT_AT",
  CHANGE_TABS_ACTIVE = "CHANGE_TABS_ACTIVE",
  CHANGE_BUSINESS_SWITCHER_ACTIVE = "CHANGE_BUSINESS_SWITCHER_ACTIVE",
  CHANGE_SELECTED_BUSINESS_ID = "CHANGE_SELECTED_BUSINESS_ID",
  CHANGE_UNREAD_MESSAGE_COUNT = "CHANGE_UNREAD_MESSAGE_COUNT",
}

export enum SidePanelIconTypeEnum {
  BASKET = "BASKET",
  FILTER = "FILTER",
  NONE = "NONE",
}

export const initialState: SellarContextState = {
  loading: false,
  supplierId: undefined,
  error: "",
  user: undefined,
  sidePanelActive: false,
  sidePanelSticky: false,
  sidePanelIcon: undefined,
  navigationButtonHidden: false,
  lastExportAt: undefined,
  tabsActive: false,
  globalNavigation: true,
  businessSwitcherActive: false,
  selectedBusinessId: undefined,
  unreadMessageCount: 0,
};

export let reducer = (
  state: SellarContextState,
  action: SellarContextAction,
): SellarContextState => {
  switch (action.type) {
    case SellarContextActionEnum.CHANGE_LAST_EXPORT_AT:
      return {
        ...state,
        lastExportAt: action.lastExportAt,
      };
    case SellarContextActionEnum.CHANGE_ERROR:
      return {
        ...state,
        error: action.error,
        loading: false,
      };
    case SellarContextActionEnum.CHANGE_SIDE_PANEL_ACTIVE:
      return {
        ...state,
        sidePanelActive: action.sidePanelActive,
      };
    case SellarContextActionEnum.CHANGE_SIDE_PANEL_STICKY:
      return {
        ...state,
        sidePanelSticky: action.sidePanelSticky,
      };
    case SellarContextActionEnum.CHANGE_SIDE_PANEL_ICON:
      return {
        ...state,
        sidePanelIcon: action.sidePanelIcon,
      };
    case SellarContextActionEnum.CHANGE_NAVIGATION_BUTTON_HIDDEN:
      return {
        ...state,
        navigationButtonHidden: action.navigationButtonHidden,
      };
    case SellarContextActionEnum.CHANGE_TABS_ACTIVE:
      return {
        ...state,
        tabsActive: action.tabsActive,
      };
    case SellarContextActionEnum.CHANGE_BUSINESS_SWITCHER_ACTIVE:
      return {
        ...state,
        businessSwitcherActive: action.businessSwitcherActive,
      };
    case SellarContextActionEnum.CHANGE_SELECTED_BUSINESS_ID:
      return {
        ...state,
        selectedBusinessId: action.selectedBusinessId,
      };
    case SellarContextActionEnum.CHANGE_UNREAD_MESSAGE_COUNT:
      return {
        ...state,
        unreadMessageCount: action.unreadMessageCount,
      };
    default:
      return state;
  }
};

export const SellarContext = createContext<
  | [state: SellarContextState, dispatch: Dispatch<SellarContextAction>]
  | undefined
>(undefined);

export const useSellarContext = () => {
  const context = useContext(SellarContext);
  if (context === undefined) {
    throw new Error("useSellarContext must be within SellarContextProvider");
  }

  return context;
};
